import { forwardRef, useCallback, useEffect, useImperativeHandle, useReducer, useRef, useState } from 'react';
import { useFramework } from '@/framework';
import { type IPoint } from '@/version-types';
import { getCaretRect, getFallbackLineSize, getSelectionRects } from '../utils';
import { useSession } from './use-session';
import './geometry.less';

export type Ref = {
    /**
     * 把客户端坐标换算为排版快照坐标。
     *
     * @param point - 客户端坐标。
     * @returns 快照坐标；无法换算时返回 null。
     */
    getPointFromClient(point: IPoint): IPoint | null;

    /**
     * 把页面坐标换算为排版快照坐标。
     *
     * @param point - 页面坐标。
     * @returns 快照坐标；无法换算时返回 null。
     */
    getPointFromPage(point: IPoint): IPoint | null;
    resetCaret(): void;
    revealCaret(): void;
};

type ClientTransform = {
    width: number;
    height: number;
    zoom: number;
    rotate: number;
};

const getSVGPointFromClient = (
    svg: SVGSVGElement | null,
    point: IPoint,
    { width, height, zoom, rotate }: ClientTransform,
): IPoint | null => {
    if (!svg || !Number.isFinite(zoom) || zoom <= 0) return null;

    const rect = svg.getBoundingClientRect();
    const x = point.x - (rect.left + rect.width / 2);
    const y = point.y - (rect.top + rect.height / 2);
    const radians = (-rotate * Math.PI) / 180;
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);

    // Safari/WebKit 的 getScreenCTM() 会漏算 HTML 祖先上的 CSS transform（此处为 BaseScaleView 的 scale），
    // 导致画布缩放后点击坐标反算错误。这里以实际屏幕包围盒中心为原点，按图层旋转和画布 zoom 做逆变换，
    // 从而绕开 getScreenCTM() 的浏览器差异，同时保持缩放和旋转场景下的光标、选区命中一致。
    return {
        x: (x * cos - y * sin) / zoom + width / 2,
        y: (x * sin + y * cos) / zoom + height / 2,
    };
};

const getSVGPointFromPage = (svg: SVGSVGElement | null, point: IPoint, transform: ClientTransform): IPoint | null => {
    const scrollX = typeof window === 'undefined' ? 0 : window.scrollX;
    const scrollY = typeof window === 'undefined' ? 0 : window.scrollY;

    return getSVGPointFromClient(
        svg,
        {
            x: point.x - scrollX,
            y: point.y - scrollY,
        },
        transform,
    );
};

export const TextEditorGeometry = forwardRef<Ref>((_, ref) => {
    const framework = useFramework();
    const session = useSession();
    const style = session ? session.adapter.getDisplayStyle(session.editingLayer) : null;
    const geometry = session ? session.adapter.getContentGeometry(session.editingLayer, session.snapshot) : null;
    const offset = geometry?.contentOffset ?? { x: 0, y: 0 };
    const fallbackHeight = style ? getFallbackLineSize(style.fontSize, style.lineSpacing) : 1;
    const caretColor = style ? style.color : '#1677ff';
    const fallbackX = geometry ? geometry.emptyCaretX : 0;
    const caret = getCaretRect(session?.snapshot ?? null, session?.selection ?? null, fallbackHeight, fallbackX);
    const rects = getSelectionRects(session?.snapshot ?? null, session?.selection ?? null);

    /**
     * 让光标组件知道“需要重新触发一次光标显示/闪烁”，这里只需要一个“递增计数器”，不关心当前值本身
     * 使用 useReducer 会比 useState 更像一个 bump() 信号：每次调用都让 caretKey 变一下
     */
    const [caretKey, bumpCaretKey] = useReducer((value: number) => value + 1, 0);

    /**
     * 短暂暂停闪烁，让光标稳定显示
     */
    const [caretSuspended, setCaretSuspended] = useState(false);

    const rootRef = useRef<SVGSVGElement>(null);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    /**
     * 重置光标状态，清理延迟恢复闪烁的定时器。
     */
    const resetCaret = useCallback(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = null;
        setCaretSuspended(false);
    }, []);

    /**
     * 立即重显光标，并短暂暂停闪烁，避免移动选区后光标处于隐藏帧。
     */
    const revealCaret = useCallback(() => {
        bumpCaretKey();
        setCaretSuspended(true);
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => setCaretSuspended(false), 500);
    }, []);

    /**
     * 在组件卸载时重置光标状态。
     */
    useEffect(() => resetCaret, [resetCaret]);

    useImperativeHandle(ref, () => {
        const transform: ClientTransform = {
            width: session?.editingLayer.width ?? 0,
            height: session?.editingLayer.height ?? 0,
            zoom: framework.service.edgelessCanvas.zoom,
            rotate: session?.editingLayer.rotate ?? 0,
        };

        /**
         * 把图层局部坐标换算为排版快照坐标。
         *
         * @param point - 图层局部坐标；无法换算时为 null。
         * @returns 命中测试直接可用的快照坐标；入参为 null 时返回 null。
         */
        const toSnapshotPoint = (point: IPoint | null): IPoint | null =>
            point ? { x: point.x - offset.x, y: point.y - offset.y } : null;

        return {
            getPointFromClient: (point) => toSnapshotPoint(getSVGPointFromClient(rootRef.current, point, transform)),
            getPointFromPage: (point) => toSnapshotPoint(getSVGPointFromPage(rootRef.current, point, transform)),
            resetCaret,
            revealCaret,
        };
    }, [framework.service.edgelessCanvas, offset.x, offset.y, resetCaret, revealCaret, session]);

    return (
        <svg ref={rootRef} className="text-editor__geometry">
            <g transform={`translate(${offset.x} ${offset.y})`}>
                {rects.map((rect, index) => (
                    <rect
                        key={index}
                        className="text-editor__selection"
                        x={rect.x}
                        y={rect.y}
                        width={rect.width}
                        height={rect.height}
                    />
                ))}
                {caret && (
                    <rect
                        key={`${caretKey}:${session?.selection.start}:${session?.selection.end}`}
                        className={`text-editor__caret${caretSuspended ? ' text-editor__caret--suspended' : ''}`}
                        x={caret.x}
                        y={caret.y}
                        width={caret.width}
                        height={caret.height}
                        fill={caretColor}
                    />
                )}
            </g>
        </svg>
    );
});

TextEditorGeometry.displayName = 'TextEditorGeometry';
